import React from 'react';
import TooltipDecimal from 'components/tooltip-decimal/tooltip-decimal';
import { formatNumberWithTooltip } from 'components/tooltip-number';
import { BaseCard } from 'components/base-card';
import style from './tract-nri-card.module.scss';

export interface TractNRIFormulaData {
  netRoyaltyAcres?: number;
  netMineralAcres?: number;
  royaltyRate?: number;
  totalTractAcres?: number;
  instrumentType?: string;
  orriBurdens?: number;
}

interface TractNRICardProps {
  calculationMethod: 'nra' | 'nma';
  tractNriFormula: TractNRIFormulaData;
  tractNri: number;
  renderTrigger: (active: boolean, toggle: () => void) => React.ReactNode;
  instrumentType?: string;
  calculationType?: 'NRI' | 'WI';
}

const FormulaVisual: React.FC<{
  calculationMethod: 'nra' | 'nma';
  instrumentType?: string;
}> = ({ calculationMethod, instrumentType }) => {
  const isNMA = calculationMethod === 'nma';
  const isOGL = instrumentType?.toUpperCase() === 'OGL';
  const isORRI = instrumentType?.toUpperCase() === 'ORRI';
  const formulaText = isNMA ? 'NMA' : '(NRA/8)';
  const resultText = 'Tract NRI';

  // ORRI + NMA formula: NMA / Tract Gross Acres = Tract NRI
  if (isORRI && isNMA) {
    return (
      <div className={style.formulaVisual}>
        <div className={style.formula}>
          <div className={style.formulaTop}>
            <div className={style.formulaText}>{formulaText}</div>
            <div className={style.formulaDivider}></div>
            <div className={style.formulaBottom}>Tract Gross Acres</div>
          </div>
        </div>
        <div className={style.rightFormula}>
          <span className={style.equalsSymbol}> = </span>
          <span>{resultText}</span>
        </div>
      </div>
    );
  }

  // OGL + NMA formula: [NMA / Total Tract Acres] × (1 - Royalty Rate - ORRI Burdens) = Tract NRI
  if (isOGL && isNMA) {
    return (
      <div className={style.formulaVisual}>
        <div className={style.bracket}>[</div>
        <div className={style.formula}>
          <div className={style.formulaTop}>
            <div className={style.formulaText}>{formulaText}</div>
            <div className={style.formulaDivider}></div>
            <div className={style.formulaBottom}>Total Tract Acres</div>
          </div>
        </div>
        <div className={style.bracket}>]</div>
        <div className={style.multiplicationSymbol}>×</div>
        <div className={style.rightFormula}>
          <span className={style.royaltyRate}>
            (1 - Royalty Rate - ORRI Burdens)
          </span>
          <span className={style.equalsSymbol}> = </span>
          <span>{resultText}</span>
        </div>
      </div>
    );
  }

  // NMA formula: NMA / Total Tract Acres × Royalty Rate = Tract NRI
  if (isNMA) {
    return (
      <div className={style.formulaVisual}>
        <div className={style.bracket}>[</div>
        <div className={style.formula}>
          <div className={style.formulaTop}>
            <div className={style.formulaText}>{formulaText}</div>
            <div className={style.formulaDivider}></div>
            <div className={style.formulaBottom}>Total Tract Acres</div>
          </div>
        </div>
        <div className={style.bracket}>]</div>
        <div className={style.multiplicationSymbol}>×</div>
        <div className={style.rightFormula}>
          <span className={style.royaltyRate}>Royalty Rate</span>
          <span className={style.equalsSymbol}> = </span>
          <span>{resultText}</span>
        </div>
      </div>
    );
  }

  // NRA formula - no Royalty Rate
  return (
    <div className={style.formulaVisual}>
      <div className={style.bracket}>[</div>
      <div className={style.formula}>
        <div className={style.formulaTop}>
          <div className={style.formulaText}>{formulaText}</div>
          <div className={style.formulaDivider}></div>
          <div className={style.formulaBottom}>Total Tract Acres (value)</div>
        </div>
      </div>
      <div className={style.bracket}>]</div>
      <div className={style.rightFormula}>
        <span className={style.equalsSymbol}> = </span>
        <span>{resultText}</span>
      </div>
    </div>
  );
};

const CalculationDisplay: React.FC<{
  calculationMethod: 'nra' | 'nma';
  tractNriFormula: TractNRIFormulaData;
  tractNri: number;
  instrumentType?: string;
}> = ({ calculationMethod, tractNriFormula, tractNri, instrumentType }) => {
  const isNMA = calculationMethod === 'nma';
  const isOGL = instrumentType?.toUpperCase() === 'OGL';
  const isORRI = instrumentType?.toUpperCase() === 'ORRI';

  // ORRI + NMA formula: NMA / Tract Gross Acres = Tract NRI
  if (isORRI && isNMA) {
    return (
      <span>
        {formatNumberWithTooltip(
          tractNriFormula.netMineralAcres ?? 0,
          2,
          '0.00',
          false,
          'ac'
        )}{' '}
        /{' '}
        {formatNumberWithTooltip(
          tractNriFormula.totalTractAcres ?? 0,
          2,
          '0.00',
          false,
          'ac'
        )}{' '}
        = <TooltipDecimal value={tractNri} precision={8} />
      </span>
    );
  }

  // OGL + NMA formula: NMA / Total Tract Acres * (1 - Royalty Rate - ORRI Burdens) = WI NRI
  if (isOGL && isNMA) {
    return (
      <span>
        {formatNumberWithTooltip(
          tractNriFormula.netMineralAcres ?? 0,
          2,
          '0.00',
          false,
          'ac'
        )}{' '}
        /{' '}
        {formatNumberWithTooltip(
          tractNriFormula.totalTractAcres ?? 0,
          2,
          '0.00',
          false,
          'ac'
        )}{' '}
        × (1 -{' '}
        <TooltipDecimal
          value={tractNriFormula.royaltyRate ?? 0}
          precision={8}
        />
        {' - '}
        <TooltipDecimal
          value={tractNriFormula.orriBurdens ?? 0}
          precision={8}
        />
        ) = <TooltipDecimal value={tractNri} precision={8} />
      </span>
    );
  }

  // NRA formula: (NRA/8) / Total Tract Acres = Tract NRI (no Royalty Rate)
  if (calculationMethod === 'nra') {
    return (
      <span>
        (
        {formatNumberWithTooltip(
          tractNriFormula.netRoyaltyAcres ?? 0,
          8,
          '0.00000000',
          false
        )}
        /8) /{' '}
        {formatNumberWithTooltip(
          tractNriFormula.totalTractAcres ?? 0,
          2,
          '0.00',
          false
        )}{' '}
        = <TooltipDecimal value={tractNri} precision={8} />
      </span>
    );
  }

  // NMA formula: NMA / Total Tract Acres * Royalty Rate = Tract NRI
  return (
    <span>
      {formatNumberWithTooltip(
        tractNriFormula.netMineralAcres ?? 0,
        2,
        '0.00',
        false,
        'ac'
      )}{' '}
      /{' '}
      {formatNumberWithTooltip(
        tractNriFormula.totalTractAcres ?? 0,
        2,
        '0.00',
        false,
        'ac'
      )}{' '}
      *{' '}
      <TooltipDecimal value={tractNriFormula.royaltyRate ?? 0} precision={8} />{' '}
      = <TooltipDecimal value={tractNri} precision={8} />
    </span>
  );
};

export const TractNRICard: React.FC<TractNRICardProps> = ({
  calculationMethod = 'nra',
  tractNriFormula,
  tractNri,
  renderTrigger,
  instrumentType,
}) => {
  const isOGL = instrumentType?.toUpperCase() === 'OGL';
  const headerText = isOGL ? 'WI NRI' : 'Tract NRI';
  const contentNode = (
    <>
      <div className={style.headerSection}>{headerText}</div>
      <div className={style.calculationSection}>
        <CalculationDisplay
          calculationMethod={calculationMethod}
          tractNriFormula={tractNriFormula}
          tractNri={tractNri}
          instrumentType={instrumentType}
        />
      </div>
      <div className={style.formulaSection}>
        <FormulaVisual
          calculationMethod={calculationMethod}
          instrumentType={instrumentType}
        />
      </div>
    </>
  );

  return (
    <span className={style.tractNriContainer}>
      <BaseCard
        contentClassName={`${style.tractNriCardContent}`}
        triggerClassName={style.cardTrigger}
        renderTrigger={renderTrigger}
        content={contentNode}
      />
    </span>
  );
};

interface TractWICardProps {
  tractWiFormula: TractNRIFormulaData;
  tractWi: number;
  renderTrigger: (active: boolean, toggle: () => void) => React.ReactNode;
}

const TractWIFormulaVisual: React.FC = () => (
  <div className={style.formulaVisual}>
    <div className={style.formula}>
      <div className={style.formulaTop}>
        <div className={style.formulaText}>NMA</div>
        <div className={style.formulaDivider}></div>
        <div className={style.formulaBottom}>Tract Gross Acres</div>
      </div>
    </div>
    <div className={style.rightFormula}>
      <span className={style.equalsSymbol}> = </span>
      <span>Tract WI</span>
    </div>
  </div>
);

const TractWICalculationDisplay: React.FC<{
  tractWiFormula: TractNRIFormulaData;
  tractWi: number;
}> = ({ tractWiFormula, tractWi }) => (
  <span>
    {formatNumberWithTooltip(
      tractWiFormula.netMineralAcres ?? 0,
      2,
      '0.00',
      false,
      'ac'
    )}{' '}
    /{' '}
    {formatNumberWithTooltip(
      tractWiFormula.totalTractAcres ?? 0,
      2,
      '0.00',
      false,
      'ac'
    )}{' '}
    = <TooltipDecimal value={tractWi} precision={8} />
  </span>
);

export const TractWICard: React.FC<TractWICardProps> = ({
  tractWiFormula,
  tractWi,
  renderTrigger,
}) => {
  const contentNode = (
    <>
      <div className={style.headerSection}>Tract WI</div>
      <div className={style.calculationSection}>
        <TractWICalculationDisplay
          tractWiFormula={tractWiFormula}
          tractWi={tractWi}
        />
      </div>
      <div className={style.formulaSection}>
        <TractWIFormulaVisual />
      </div>
    </>
  );

  return (
    <span className={style.tractNriContainer}>
      <BaseCard
        contentClassName={`${style.tractNriCardContent}`}
        triggerClassName={style.cardTrigger}
        renderTrigger={renderTrigger}
        content={contentNode}
      />
    </span>
  );
};
